import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const CORRECT_PASSWORD = 'nothingtoseehere2025';
const PASSWORD_KEY = 'app_password_authenticated';

interface PasswordGateProps {
  children: React.ReactNode;
}

export const PasswordGate: React.FC<PasswordGateProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Bypass password gate if disabled via env var (for E2E tests)
    if (import.meta.env.VITE_DISABLE_PASSWORD_GATE === 'true') {
      setIsAuthenticated(true);
      setIsChecking(false);
      return;
    }

    const stored = sessionStorage.getItem(PASSWORD_KEY);
    if (stored === 'true') {
      setIsAuthenticated(true);
    }
    setIsChecking(false);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (password === CORRECT_PASSWORD) {
      sessionStorage.setItem(PASSWORD_KEY, 'true');
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-app-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bet99-primary mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-app-background p-4">
        <div className="w-full max-w-sm bg-app-surface rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">Protected Access</h1>
            <p className="text-text-secondary">
              Please enter the password to continue, {import.meta.env.VITE_DISABLE_PASSWORD_GATE}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                data-test-id="password-input"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full"
                autoFocus
              />
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            </div>

            <Button
              data-test-id="unlock-btn"
              type="submit"
              variant="bet99"
              className="w-full"
              disabled={!password}
            >
              Unlock
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
